import React from 'react';
import { Close } from '@mui/icons-material';
import { Box, useTheme } from '@mui/material';
import { RevampedIconPath } from '@askporter/utils';
import { Button, IconButton, RevampedIcon, RevampedIconSize } from '../../';
import { CircleWrapper } from './CircleWrapper';

export type AlertType = 'warning' | 'info' | 'error' | 'success' | 'brand';

export interface AlertBoxProps {
  alertType: AlertType;
  backgroundMode?: 'filled' | 'transparent';
  alertBoxMargin?: string;
  onClose?: () => void;
  primaryAction?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  secondaryAction?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
}
export interface AlertData {
  borderColor: string;
  backgroundColor: string;
  iconColor: string;
  fullPath: string;
}
const AlertTypeColorMap: Record<AlertType, AlertData> = {
  warning: {
    borderColor: '#DC6803',
    backgroundColor: '#FFFAEB',
    iconColor: '#DC6803',
    fullPath: '/media/icons/status/01-general/04-error-circle/20px.svg',
  },
  info: {
    borderColor: '#667085',
    backgroundColor: '#F9FAFB',
    iconColor: '#667085',
    fullPath: '/media/icons/status/01-general/05-information-circle/20px.svg',
  },
  error: {
    borderColor: '#D92D20',
    backgroundColor: '#FEF3F2',
    iconColor: '#D92D20',
    fullPath: '/media/icons/status/01-general/04-error-circle/20px.svg',
  },
  success: {
    borderColor: '#079455',
    backgroundColor: '#ECFDF3',
    iconColor: '#079455',
    fullPath: '/media/icons/status/01-general/02-success-circle/20px.svg',
  },
  brand: {
    borderColor: '#D0D5DD',
    backgroundColor: '#F9FAFB',
    iconColor: '#667185',
    fullPath: '/media/icons/status/01-general/05-information-circle/20px.svg',
  },
};

export const AlertBox: React.FC<React.PropsWithChildren<AlertBoxProps>> = ({
  children,
  alertType,
  backgroundMode = 'filled',
  alertBoxMargin = 0,
  onClose,
  primaryAction,
  secondaryAction,
}) => {
  const {
    palette: { primary },
  } = useTheme();
  let alertData: AlertData = {
    borderColor: primary.dark,
    backgroundColor: '#F3EDFD',
    iconColor: primary.dark,
    fullPath: '/media/icons/status/01-general/05-information-circle/20px.svg',
  };
  if (alertType !== 'brand') {
    alertData = AlertTypeColorMap[alertType];
  }

  return (
    <Box
      sx={{
        border: `1px solid ${alertData.borderColor}`,
        borderRadius: '10px',
        backgroundColor: backgroundMode === 'filled' ? alertData.backgroundColor : 'transparent',
        p: '6px',
        boxShadow: '0px 1px 2px 0px #1018280D',
        margin: alertBoxMargin,
        position: 'relative',
      }}
      role="alert"
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box>
          <CircleWrapper outerCircleSize={36} innerCircleSize={26} centreColor={alertData.iconColor}>
            <RevampedIcon
              fullPath={alertData.fullPath as RevampedIconPath}
              customSvgStyles={{
                '& svg > g > path': {
                  stroke: alertData.iconColor,
                },
              }}
              iconSize={RevampedIconSize.SM}
            />
          </CircleWrapper>
        </Box>
        <Box sx={{ ml: '7px', flex: 1 }}>
          {children}
          <Box sx={{ mb: '10px', mt: '12px' }}>
            {secondaryAction && (
              <Button
                component={secondaryAction.href ? 'a' : 'button'}
                href={secondaryAction.href}
                variant="textLinked"
                color="secondary"
                onClick={secondaryAction.onClick}
                sx={{ mr: '12px' }}
                size="small"
              >
                {secondaryAction.label}
              </Button>
            )}
            {primaryAction && (
              <Button
                component={primaryAction.href ? 'a' : 'button'}
                href={primaryAction.href}
                variant="textLinked"
                onClick={primaryAction.onClick}
                size="small"
              >
                {primaryAction.label}
              </Button>
            )}
          </Box>
        </Box>
        {onClose && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{ p: '0', position: 'absolute', right: '8px', top: '8px' }}
          >
            <Close sx={{ color: '#98A2B3' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
